import { ReactNode, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import PackageDocViewer from "./PackageDocViewer";
import {
  DISCIPLINES,
  PACKAGE_TYPES,
  PackageDraft,
  PackageType,
  WorkArea,
  WorkPackage,
  cwpsLinking,
} from "./types";

/**
 * Which half of a package the form is for.
 *
 *   details → what the package IS — read from its document, so edited only to fix a bad read:
 *             type, number, title, discipline, revision, scope, boundaries, contractor, owner,
 *             PWP procurement category. (The pencil.)
 *   plan    → where it SITS in the plan — decided by the planner, never in the document: work area,
 *             links, dates, hours, schedule activities, PWP ROS and long lead. (The lightning bolt.)
 *
 * Both halves are held in state and the whole package is sent on save, so editing one can never
 * blank the other.
 */
export type PackageFormMode = "details" | "plan";

interface PackageModalProps {
  pkg: WorkPackage;
  mode: PackageFormMode;
  /** For fetching the package's document into the viewer. */
  projectId: string;
  /** The rest of the project's register — what the link lists offer. */
  register: WorkPackage[];
  /** The project's work areas — what the CWA dropdown offers. */
  cwas: WorkArea[];
  onCancel: () => void;
  /**
   * Saves the package. `cwpIds` is passed only when an EWP's or PWP's CWP links were changed here —
   * those are written onto the CWPs, not onto this package.
   */
  onSubmit: (draft: PackageDraft, cwpIds?: string[]) => Promise<void>;
}

/**
 * The form's body, alone or beside the package's document. With a document it is the engineering
 * upload's verify layout: the page on the left, the fields to settle against it on the right.
 */
const ModalBody = ({ doc, children }: { doc: ReactNode; children: ReactNode }) =>
  doc ? (
    <div className="vr-modal-body">
      <div className="vr-split">
        {doc}
        <div className="vr-review-pane vr-pkg-fields">{children}</div>
      </div>
    </div>
  ) : (
    <div className="vr-modal-body">{children}</div>
  );

const PackageModal = ({ pkg, mode, projectId, register, cwas, onCancel, onSubmit }: PackageModalProps) => {
  // Details are what the document says, so they are checked against it. Plan & link is decided by
  // the planner and needs no document beside it.
  const showDoc = mode === "details" && !!pkg.source;
  const [type, setType] = useState<PackageType>(pkg.type);
  const [packageNo, setPackageNo] = useState(pkg.packageNo);
  const [title, setTitle] = useState(pkg.title);
  const [discipline, setDiscipline] = useState(pkg.discipline ?? "");
  const [revision, setRevision] = useState(pkg.revision ?? "");
  const [cwaId, setCwaId] = useState(pkg.cwaId ?? "");
  const [hours, setHours] = useState(pkg.hours?.toString() ?? "");
  const [plannedStart, setPlannedStart] = useState(pkg.plannedStart ?? "");
  const [plannedFinish, setPlannedFinish] = useState(pkg.plannedFinish ?? "");
  const [needDate, setNeedDate] = useState(pkg.needDate ?? "");
  const [activityIds, setActivityIds] = useState((pkg.scheduleActivityIds ?? []).join(", "));
  const [contractor, setContractor] = useState(pkg.contractor ?? "");
  const [owner, setOwner] = useState(pkg.owner ?? "");
  const [scope, setScope] = useState(pkg.scope ?? "");
  const [boundaries, setBoundaries] = useState(pkg.boundaries ?? "");
  const [ewpIds, setEwpIds] = useState<string[]>(pkg.ewpIds ?? []);
  const [pwpIds, setPwpIds] = useState<string[]>(pkg.pwpIds ?? []);
  const [feeds, setFeeds] = useState<string[]>(pkg.feedsPwpIds ?? []);
  const [procurementCategory, setProcurementCategory] = useState(pkg.procurementCategory ?? "");
  const [rosDate, setRosDate] = useState(pkg.rosDate ?? "");
  const [longLead, setLongLead] = useState(pkg.longLead ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const others = useMemo(() => register.filter((p) => p.id !== pkg.id), [register, pkg]);
  const ofType = (t: PackageType) =>
    others.filter((p) => p.type === t).sort((a, b) => a.packageNo.localeCompare(b.packageNo));
  const ewps = useMemo(() => ofType("EWP"), [others]); // eslint-disable-line react-hooks/exhaustive-deps
  const pwps = useMemo(() => ofType("PWP"), [others]); // eslint-disable-line react-hooks/exhaustive-deps
  const cwps = useMemo(() => ofType("CWP"), [others]); // eslint-disable-line react-hooks/exhaustive-deps
  /** EWP / PWP: the CWPs it is on. Saved onto those CWPs — see onSubmit's second argument. */
  const linkedFrom = useMemo(() => cwpsLinking(pkg, register).map((c) => c.id), [pkg, register]);
  const [cwpIds, setCwpIds] = useState<string[]>(linkedFrom);
  const areas = useMemo(
    () =>
      [...cwas].sort(
        (a, b) =>
          (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER) ||
          a.code.localeCompare(b.code),
      ),
    [cwas],
  );

  const ready = packageNo.trim().length > 0 && title.trim().length > 0;

  const toggle = (setter: (fn: (prev: string[]) => string[]) => void, id: string) =>
    setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    const t = (v: string) => v.trim() || undefined;
    const base: PackageDraft = {
      type,
      packageNo: packageNo.trim(),
      title: title.trim(),
      discipline: t(discipline),
      revision: t(revision),
      cwaId: t(cwaId),
      hours: hours.trim() === "" ? undefined : Number(hours),
      plannedStart: t(plannedStart),
      plannedFinish: t(plannedFinish),
      needDate: t(needDate),
      scheduleActivityIds: activityIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      contractor: t(contractor),
      owner: t(owner),
      scope: t(scope),
      boundaries: t(boundaries),
      _etag: pkg._etag,
    };
    const draft: PackageDraft =
      type === "CWP"
        ? { ...base, ewpIds, pwpIds }
        : type === "EWP"
          ? { ...base, feedsPwpIds: feeds }
          : { ...base, procurementCategory: t(procurementCategory), rosDate: t(rosDate), longLead };
    try {
      const linksChanged =
        mode === "plan" &&
        type !== "CWP" &&
        type === pkg.type &&
        (cwpIds.length !== linkedFrom.length || cwpIds.some((id) => !linkedFrom.includes(id)));
      await onSubmit(draft, linksChanged ? cwpIds : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the package.");
      setSaving(false);
    }
  };

  const pickList = (
    label: string,
    options: WorkPackage[],
    picked: string[],
    onToggle: (id: string) => void,
    empty: string,
    hint?: string,
  ) => (
    <div className="vr-field">
      <label>{label}</label>
      <div className="vr-pick-list vr-pkg-pick">
        {options.length === 0 && <div className="vr-empty">{empty}</div>}
        {options.map((p) => (
          <label key={p.id} className="vr-pick-row">
            <input type="checkbox" checked={picked.includes(p.id)} onChange={() => onToggle(p.id)} />
            <span className="vr-pick-body">
              <span className="vr-pick-name">
                {p.packageNo} – {p.title}
              </span>
            </span>
          </label>
        ))}
      </div>
      {hint && <span className="vr-field-hint">{hint}</span>}
    </div>
  );

  const input = (id: string, label: string, value: string, set: (v: string) => void, kind = "text") => (
    <div className="vr-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="vr-input"
        type={kind}
        min={kind === "number" ? 0 : undefined}
        value={value}
        onChange={(e) => set(e.target.value)}
      />
    </div>
  );

  return (
    <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Work package">
      <div className={`vr-modal${showDoc ? " vr-modal-wide" : ""}`}>
        <div className="vr-modal-head">
          <div>
            <h3>{mode === "details" ? `Edit ${pkg.packageNo}` : `Plan & link ${pkg.packageNo}`}</h3>
            <p>
              {mode === "details"
                ? "Correct what was read from the document. This does not change the package's status."
                : "Place the package in the plan: its work area, links and dates."}
            </p>
          </div>
          <button className="vr-rowbtn vr-icononly" onClick={onCancel} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <ModalBody doc={showDoc ? <PackageDocViewer projectId={projectId} pkg={pkg} /> : null}>
          {/* ── Details: what the package is ── */}
          {mode === "details" && (
            <>
              <div className="vr-field-grid">
                <div className="vr-field">
                  <label htmlFor="vr-pkg-type">Package type</label>
                  <select
                    id="vr-pkg-type"
                    className="vr-select"
                    value={type}
                    onChange={(e) => setType(e.target.value as PackageType)}
                  >
                    {PACKAGE_TYPES.map((pt) => (
                      <option key={pt} value={pt}>
                        {pt}
                      </option>
                    ))}
                  </select>
                </div>
                {input("vr-pkg-no", "Package number", packageNo, setPackageNo)}
              </div>

              {input("vr-pkg-title", "Title", title, setTitle)}

              <div className="vr-field-grid">
                <div className="vr-field">
                  <label htmlFor="vr-pkg-disc">Discipline</label>
                  <select
                    id="vr-pkg-disc"
                    className="vr-select"
                    value={discipline}
                    onChange={(e) => setDiscipline(e.target.value)}
                  >
                    <option value="">Select discipline</option>
                    {discipline && !(DISCIPLINES as readonly string[]).includes(discipline) && (
                      <option value={discipline}>{discipline}</option>
                    )}
                    {DISCIPLINES.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                {input("vr-pkg-rev", "Revision", revision, setRevision)}
              </div>

              <div className="vr-field-grid">
                {input("vr-pkg-contractor", "Contractor", contractor, setContractor)}
                {input("vr-pkg-owner", "Owner", owner, setOwner)}
              </div>

              {type === "PWP" && (
                <div className="vr-field">
                  <label htmlFor="vr-pkg-proc">Procurement category</label>
                  <input
                    id="vr-pkg-proc"
                    className="vr-input"
                    value={procurementCategory}
                    onChange={(e) => setProcurementCategory(e.target.value)}
                    placeholder="Bulk Materials, Engineered Equipment…"
                  />
                </div>
              )}

              <div className="vr-field">
                <label htmlFor="vr-pkg-scope">Scope description</label>
                <textarea
                  id="vr-pkg-scope"
                  className="vr-input vr-textarea"
                  rows={3}
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                />
              </div>

              <div className="vr-field">
                <label htmlFor="vr-pkg-bound">Boundaries / battery limits</label>
                <textarea
                  id="vr-pkg-bound"
                  className="vr-input vr-textarea"
                  rows={3}
                  value={boundaries}
                  onChange={(e) => setBoundaries(e.target.value)}
                />
              </div>
            </>
          )}

          {/* ── Plan & link: where the package sits ── */}
          {mode === "plan" && (
            <>
              <div className="vr-field">
                <label htmlFor="vr-pkg-cwa">Construction work area</label>
                <select
                  id="vr-pkg-cwa"
                  className="vr-select"
                  value={cwaId}
                  onChange={(e) => setCwaId(e.target.value)}
                >
                  <option value="">Select CWA</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code}
                      {a.name ? ` — ${a.name}` : ""}
                    </option>
                  ))}
                </select>
                {areas.length === 0 && (
                  <span className="vr-field-hint">
                    No work areas on this project yet — an administrator adds them when editing the project.
                  </span>
                )}
              </div>

              {type === "CWP" && (
                <>
                  {pickList(
                    "Linked EWPs",
                    ewps,
                    ewpIds,
                    (id) => toggle(setEwpIds, id),
                    "No EWPs in this project yet.",
                    "The engineering this construction work depends on.",
                  )}
                  {pickList(
                    "Linked PWPs",
                    pwps,
                    pwpIds,
                    (id) => toggle(setPwpIds, id),
                    "No PWPs in this project yet.",
                    "The materials and equipment it needs on site.",
                  )}
                </>
              )}

              {type === "EWP" &&
                pickList(
                  "Feeds PWPs",
                  pwps,
                  feeds,
                  (id) => toggle(setFeeds, id),
                  "No PWPs in this project yet.",
                  "The procurement packages that need this engineering before they can be bought.",
                )}

              {/* Linkable from this end too. The link is still stored on the CWP — saving here
                  updates the CWPs ticked and unticked — so both ends always agree. Only offered
                  while the type is unchanged: a link belongs to what the package is now. */}
              {type !== "CWP" &&
                type === pkg.type &&
                pickList(
                  "Linked CWPs",
                  cwps,
                  cwpIds,
                  (id) => toggle(setCwpIds, id),
                  "No CWPs in this project yet.",
                  `The construction work this ${type === "EWP" ? "engineering" : "procurement"} package supports.`,
                )}

              <div className="vr-field-grid">
                {input("vr-pkg-start", "Planned start", plannedStart, setPlannedStart, "date")}
                {input("vr-pkg-finish", "Planned finish", plannedFinish, setPlannedFinish, "date")}
              </div>

              <div className="vr-field-grid">
                {type === "PWP"
                  ? input("vr-pkg-ros", "Required on site (ROS)", rosDate, setRosDate, "date")
                  : input("vr-pkg-need", "Need date", needDate, setNeedDate, "date")}
                {input("vr-pkg-hours", "Estimated work hours", hours, setHours, "number")}
              </div>

              {input("vr-pkg-acts", "Schedule activity IDs (comma separated)", activityIds, setActivityIds)}

              {type === "PWP" && (
                <label className="vr-pick-row">
                  <input type="checkbox" checked={longLead} onChange={(e) => setLongLead(e.target.checked)} />
                  <span className="vr-pick-body">
                    <span className="vr-pick-name">Long-lead item</span>
                  </span>
                </label>
              )}
            </>
          )}

          {error && <div className="vr-form-error">{error}</div>}
        </ModalBody>

        <div className="vr-modal-foot">
          <button className="vr-btn vr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="vr-btn vr-btn-glow" onClick={() => void save()} disabled={!ready || saving}>
            {saving ? (
              <>
                <Loader2 size={14} className="vr-spin-inline" /> Saving…
              </>
            ) : (
              "Save changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageModal;
